import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail } from 'lucide-react';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const { user, updateUser } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [pageStatus, setPageStatus] = useState('input');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Handle redirect from old GET endpoint
    if (status === 'success' || status === 'already') { setPageStatus('success'); return; }
    if (status === 'error') { setPageStatus('error'); return; }
  }, [status]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    // Auto-focus next
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    if (pasted.length > 0) {
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleSubmit = async () => {
    const token = code.join('');
    if (token.length !== 6) { toast.error('Entrez le code à 6 chiffres'); return; }
    setLoading(true);
    try {
      await authAPI.verifyEmail({ token });
      setPageStatus('success');
      if (user) updateUser({ ...user, email_verified: true });
      toast.success('Email vérifié !');
    } catch {
      toast.error('Code invalide ou expiré');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const email = user?.email;
    if (!email) { toast.error('Connectez-vous d\'abord'); return; }
    try {
      await authAPI.resendVerification({ email });
      toast.success('Nouveau code envoyé !');
    } catch {
      toast.error('Erreur lors du renvoi');
    }
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.every(d => d !== '') && pageStatus === 'input') {
      handleSubmit();
    }
  }, [code]);

  return (
    <div className="min-h-screen bg-[#E8F4FD] flex items-center justify-center py-12 px-4" data-testid="verify-email-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <img src="/assets/padsmash_logo_main.png" alt="PadSmash" className="h-24 sm:h-28 w-auto mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D6E8F0] text-center">
          {pageStatus === 'success' ? (
            <div data-testid="verify-success">
              <CheckCircle className="w-16 h-16 text-[#32CD32] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Email vérifié !</h1>
              <p className="text-[#5A8BA8] mb-6">Votre adresse email a été confirmée.</p>
              <Link to="/catalogue">
                <Button className="btn-primary">Voir les annonces</Button>
              </Link>
            </div>
          ) : pageStatus === 'error' ? (
            <div data-testid="verify-error">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Lien invalide</h1>
              <p className="text-[#5A8BA8] mb-6">Ce lien de vérification est invalide ou a déjà été utilisé.</p>
              <Link to="/login"><Button className="bg-[#0C63BF]">Se connecter</Button></Link>
            </div>
          ) : (
            <div data-testid="verify-code-form">
              <div className="w-16 h-16 bg-[#E8F4FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#0C63BF]" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Vérifiez votre email</h1>
              <p className="text-[#5A8BA8] text-sm mb-6">
                Entrez le code à 6 chiffres envoyé à<br />
                <strong className="text-[#033871]">{user?.email || 'votre adresse email'}</strong>
              </p>

              <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold text-[#033871] border-2 border-[#D6E8F0] rounded-xl focus:border-[#0C63BF] focus:ring-2 focus:ring-[#0C63BF]/20 outline-none transition-all"
                    data-testid={`verify-code-${i}`}
                  />
                ))}
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading || code.some(d => d === '')}
                className="w-full btn-primary h-12 mb-4"
                data-testid="verify-submit"
              >
                {loading ? 'Vérification...' : 'Vérifier'}
              </Button>

              <p className="text-sm text-[#5A8BA8]">
                Pas reçu le code ?{' '}
                <button onClick={handleResend} className="text-[#0C63BF] font-semibold hover:underline" data-testid="resend-code-btn">
                  Renvoyer
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
